import { useCallback } from 'react'

import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Tag } from 'antd'
import { Popover, Tooltip } from 'govwell-ui'
import { MenuContentStyles } from 'govwell-ui/components/Menu/Menu'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import FilterEditor from 'src/components/TableViews/FilterEditor'
import Text, { TextSize } from 'src/components/Typography/Text'
import { TableViewMode } from 'src/fetch/views'
import useDisclosure from 'src/hooks/use-disclosure'
import { TableViewFilter } from 'src/models/TableViews/TableViewFilter'

import FilterTagValue from './FilterTagValue'

const StyledXButton = styled(Text)`
  outline: solid 1px transparent;
  border-radius: 4px;
  margin-left: 3px;
  padding: 0px 3px;
  cursor: pointer;
  align-self: center;
  &:focus-visible {
    outline: solid 1px ${({ theme }) => theme.colorPrimaryActive};
  }
`
const StyledPopoverContent = styled(Popover.Content)`
  ${MenuContentStyles}
  width: fit-content;
  max-width: 100vw;
  max-height: var(--radix-popover-content-available-height);
  overflow: auto;
  padding: 6px 0px;
`

const StyledBadge = styled.div`
  background-color: ${({ theme }) => theme['orange-6']};
  width: 8px;
  height: 8px;
  border-radius: 8px;
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
`

// Match the Antd Button's `borderRadius` token (6px in our theme)
// instead of Tag's default `borderRadiusSM` (4px), so filter chips
// align visually with the adjacent "Add filters" button.
const StyledTag = styled(Tag)<{ $isLocked?: boolean; $isEditable?: boolean }>`
  margin: 0;
  height: fit-content;
  max-width: calc(100% - 4px);
  border-radius: ${({ theme }) => theme.borderRadius}px;
  display: flex;
  width: fit-content;
  align-items: center;
  gap: 3px;
  position: relative;
  ${({ $isLocked, theme }) =>
    $isLocked
      ? `
    background-color: ${theme.colorFillAlter};
    border-color: ${theme.colorBorderSecondary};
    cursor: default;
  `
      : ''}
  ${({ $isEditable }) =>
    $isEditable
      ? `
    cursor: pointer;
    pointer-events: all;
  `
      : ''}
`

const ENTER = 'Enter'
const SPACE = ' '

const LOCKED_FILTER_TOOLTIP =
  "This is a default filter for the selected report type and can't be changed."

type Props = {
  filter: TableViewFilter
  tableViewMode: TableViewMode
  /**
   * Suppress the orange "unsaved chip edit" badge. Set by surfaces where
   * the entire form is implicitly unsaved until the parent CTA fires
   * (e.g. the Report Configuration drawer), so a per-chip dirty hint just
   * adds visual noise unrelated to the open → edit → run flow.
   */
  hideUnsavedBadge?: boolean
  /** Registry default filter — read-only chip (gray, no remove, no editor). */
  isLocked?: boolean
}
const FilterTag = ({ filter, tableViewMode, hideUnsavedBadge, isLocked }: Props) => {
  const { isOpen: isEditorOpen, open: onOpenEditor, close: onCloseEditor } = useDisclosure()
  const handleClear = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation()
      runInAction(() => {
        filter.clear()
      })
      onCloseEditor()
    },
    [filter, onCloseEditor]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ([ENTER, SPACE].includes(e.key)) {
        handleClear()
      }
    },
    [handleClear]
  )

  if ((!filter.isPopulated && !isEditorOpen) || !filter.columnTemplate) {
    return null
  }

  const tagContents = (
    <>
      <Text colorToken="colorTextSecondary">{filter.columnTemplate?.labelSentenceCase}: </Text>
      <Text strong whiteSpace="break-spaces">
        <FilterTagValue filter={filter} />
      </Text>
      {!isLocked && (
        <StyledXButton
          role="button"
          size={TextSize.Small}
          onClick={handleClear}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <FontAwesomeIcon icon={faXmark} />
        </StyledXButton>
      )}
      {filter.hasUnsavedChanges &&
        tableViewMode === TableViewMode.Edit &&
        !hideUnsavedBadge &&
        !isLocked && <StyledBadge />}
    </>
  )

  if (isLocked) {
    return (
      <Tooltip content={LOCKED_FILTER_TOOLTIP}>
        <StyledTag key={filter.columnTemplateGeneratedId} $isLocked>
          {tagContents}
        </StyledTag>
      </Tooltip>
    )
  }

  return (
    <Popover isOpen={isEditorOpen} onOpen={onOpenEditor} onClose={onCloseEditor}>
      <Popover.Trigger asChild>
        <StyledTag key={filter.columnTemplateGeneratedId} $isEditable onClick={onOpenEditor}>
          {tagContents}
        </StyledTag>
      </Popover.Trigger>
      <StyledPopoverContent align="start" sideOffset={6}>
        <FilterEditor columnTemplate={filter.columnTemplate} />
      </StyledPopoverContent>
    </Popover>
  )
}

export default observer(FilterTag)
